import { type DatumAbstraction, type WallDatumAbstraction } from "building-types/datums/datumAbstractionTypes";
import type { FacadeDatumTable, FacadeHorizontalDatum, FacadeVerticalDatum } from "building-types/datums/datumsTypes";
import { DatumType } from "building-types/datums/datumsTypes";
import {
  HorizontalJustification,
  VerticalJustification,
  WallVerticalDatumAnchor,
} from "building-types/datums/datumsTypesEnum";
import type { DatumUsage } from "building-types/datums/datumUsageTypes";
import { DatumVerticalUsageType, DatumHorizontalUsageType } from "building-types/datums/datumUsageTypes";
import type {
  Facade,
  FacadeSpanStratum,
  FacadeSweepStratum,
  SideWallSweeps,
  WallFacadeLabel,
  WallFacadeTable,
  WallSweepRail,
  WallSweeps,
} from "building-types/facades/facadeTypes";
import { SpanExtrema } from "building-types/facades/facadeTypesEnum";
import type { Label } from "building-types/facades/label";
import type { FacadeWallSweep } from "building-types/facades/wallSweep";
import type { ExteriorMaterial } from "building-types/materials/exteriorMaterialTypes";
import type { HalfEdgeAssociationTable } from "building-types/planarGraphs/planarGraphAssociationTypes";
import type { RWTable, Table } from "common/data/table";
import type { WorldInches } from "common/types/nominalNumbersTypes";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { degreeToRadian } from "common/utils/math";
import uid from "common/utils/uid";
import { duplicateFacadeWallSweep } from "doc/utils/facadeWallSweepTools";
import { exteriorMaterialGeometryNew } from "doc/utils/materialTools";
import type { Face } from "geom/brep";
import { DIST_EPSILON, ZERO_EPSILON } from "geom/core";
import { ray2PolyLine2IntersectionMakeUnique } from "geom/intersect";
import { intersectRay2PolyLine2 } from "geom/intersectRay2";
import { planarGraphAdjacentHalfEdge } from "geom/planarGraph";
import { planeProject } from "geom/plane";
import { polyLine2AABB2, polyLine2ByTwoPoints, polyLine2Extend, polyLine2Length } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { PolyLine3 } from "geom/polyLine3";
import type { UVTransformation } from "geom/uv";
import { vec2Rotate } from "geom/vec2";
import { getDatumAbstraction } from "../elements/datums/datumAbstractions";

export function wallSweepRailNew(
  rail: PolyLine3,
  profileName: string,
  profileId?: string,
  material?: ExteriorMaterial,
): WallSweepRail {
  return {
    rail,
    profileName,
    profileId,
    material,
  };
}

export function sideWallSweepsNew(): SideWallSweeps {
  return {
    geometry: exteriorMaterialGeometryNew(),
    rails: [],
  };
}

export function wallSweepsNew(): WallSweeps {
  return {
    leftWallSweeps: sideWallSweepsNew(),
    rightWallSweeps: sideWallSweepsNew(),
  };
}

export function facadeHorizontalDatumNew(
  name: string,
  justification: HorizontalJustification,
  offset: number,
  offsetNormalized: boolean,
): FacadeHorizontalDatum {
  return {
    catalogItemType: "DATUM",
    name,
    datumType: DatumType.HORIZONTAL,
    justification,
    offset,
    offsetNormalized,
  };
}

export function facadeVerticalDatumNew(
  name: string,
  anchor: WallVerticalDatumAnchor,
  justification: VerticalJustification,
  offset: number,
  offsetNormalized: boolean,
): FacadeVerticalDatum {
  return {
    catalogItemType: "DATUM",
    name,
    datumType: DatumType.VERTICAL,
    anchor,
    justification,
    offset,
    offsetNormalized,
  };
}

export function facadeSpanStratumNew(
  id: string,
  name: string,
  sortIndex: number,
  material: ExteriorMaterial,
  lowerDatumUsage: DatumUsage<DatumVerticalUsageType.WALL_VERTICAL>,
  upperDatumUsage: DatumUsage<DatumVerticalUsageType.WALL_VERTICAL>,
  startDatumUsage: DatumUsage<DatumHorizontalUsageType.WALL_HORIZONTAL>,
  endDatumUsage: DatumUsage<DatumHorizontalUsageType.WALL_HORIZONTAL>,
  parentId: string,
  materialTransformation?: Partial<UVTransformation>,
  materialThickness?: number,
  elevationNoteEnabled?: boolean,
  elevationNote?: string,
): FacadeSpanStratum {
  return {
    id,
    name,
    sortIndex,
    entityType: "FACADE_SPAN_STRATUM",
    parentId,
    material,
    lowerDatumUsage,
    upperDatumUsage,
    startDatumUsage,
    endDatumUsage,
    materialTransformation,
    materialThickness,
    elevationNoteEnabled,
    elevationNote,
  };
}

export function duplicateFacadeSpanStratum(original: FacadeSpanStratum, name?: string): FacadeSpanStratum {
  return {
    id: uid(),
    entityType: "FACADE_SPAN_STRATUM",
    sortIndex: original.sortIndex,
    parentId: original.parentId,
    material: original.material,
    materialTransformation: original.materialTransformation,
    materialThickness: original.materialThickness,
    elevationNoteEnabled: original.elevationNoteEnabled,
    elevationNote: original.elevationNote,
    name: name ?? original.name,
    lowerDatumUsage: original.lowerDatumUsage,
    upperDatumUsage: original.upperDatumUsage,
    startDatumUsage: original.startDatumUsage,
    endDatumUsage: original.endDatumUsage,
  };
}

export function facadeSweepStratumNew(
  id: string,
  name: string,
  sortIndex: number,
  material: ExteriorMaterial,
  datumUsage: DatumUsage<DatumVerticalUsageType.WALL_VERTICAL>,
  sweep: FacadeWallSweep,
  parentId?: string,
  materialTransformation?: UVTransformation,
  materialThickness?: number,
): FacadeSweepStratum {
  return {
    id,
    name,
    sortIndex,
    parentId,
    entityType: "FACADE_SWEEP_STRATUM",
    material,
    datumUsage,
    sweep,
    materialTransformation,
    materialThickness,
  };
}

export function duplicateFacadeSweepStratum(original: FacadeSweepStratum, name?: string): FacadeSweepStratum {
  return {
    id: uid(),
    entityType: "FACADE_SWEEP_STRATUM",
    sortIndex: original.sortIndex,
    parentId: original.parentId,
    material: original.material,
    materialTransformation: original.materialTransformation,
    materialThickness: original.materialThickness,
    datumUsage: original.datumUsage,
    name: name ?? original.name,
    sweep: duplicateFacadeWallSweep(original.sweep),
  };
}

export function getDefaultJustification(anchor: WallVerticalDatumAnchor): VerticalJustification {
  switch (anchor) {
    case WallVerticalDatumAnchor.BASE_WALL:
      return VerticalJustification.TOP_DOWN;
    case WallVerticalDatumAnchor.GABLE_END:
      return VerticalJustification.BOTTOM_UP;
    default:
      assertUnreachable(anchor);
  }
}

// instantiate facade and validate components

type FacadeNewParams = Omit<Facade, "entityType">;

export function facadeNew({
  name,
  cornerBoardBehavior = null,
  insideCornerBoardBehavior = null,
  cornerBoardOffsetZ = 0,
  wainscotingBehavior = null,
  sortIndex = 0,
  id = uid(),
}: Partial<FacadeNewParams>): WallFacadeLabel {
  const ret = {
    label: name,
    wallFacade: {
      entityType: "FACADE",
      id,
      sortIndex,
      name,
      cornerBoardBehavior,
      insideCornerBoardBehavior,
      cornerBoardOffsetZ,
      wainscotingBehavior,
    },
  } as WallFacadeLabel;

  return ret;
}

export const baseWallBottomDatumId = "base_wall_bottom";
export const baseWallTopDatumId = "base_wall_top";
export const gableEndBottomDatumId = "gable_end_bottom";
export const gableEndTopDatumId = "gable_end_top";
export const wallStartDatumId = "wall_start";
export const wallEndDatumId = "wall_end";

const defaultBaseWallBottomDatum: FacadeVerticalDatum = facadeVerticalDatumNew(
  SpanExtrema.BASE_WALL_BOTTOM,
  WallVerticalDatumAnchor.BASE_WALL,
  VerticalJustification.BOTTOM_UP,
  0,
  false,
);

const defaultBaseWallTopDatum: FacadeVerticalDatum = facadeVerticalDatumNew(
  SpanExtrema.BASE_WALL_TOP,
  WallVerticalDatumAnchor.BASE_WALL,
  VerticalJustification.TOP_DOWN,
  0,
  false,
);

const defaultGableEndBottomDatum: FacadeVerticalDatum = facadeVerticalDatumNew(
  SpanExtrema.GABLE_END_BOTTOM,
  WallVerticalDatumAnchor.GABLE_END,
  VerticalJustification.BOTTOM_UP,
  0,
  false,
);

const defaultGableEndTopDatum: FacadeVerticalDatum = facadeVerticalDatumNew(
  SpanExtrema.GABLE_END_TOP,
  WallVerticalDatumAnchor.GABLE_END,
  VerticalJustification.TOP_DOWN,
  0,
  false,
);

const defaultWallStartDatum: FacadeHorizontalDatum = facadeHorizontalDatumNew(
  SpanExtrema.WALL_START,
  HorizontalJustification.START_TO_END,
  0,
  false,
);

const defaultWallEndDatum: FacadeHorizontalDatum = facadeHorizontalDatumNew(
  SpanExtrema.WALL_END,
  HorizontalJustification.END_TO_START,
  0,
  false,
);

export const defaultFacadeDatumsInternal = {
  [baseWallBottomDatumId]: defaultBaseWallBottomDatum,
  [baseWallTopDatumId]: defaultBaseWallTopDatum,
  [gableEndBottomDatumId]: defaultGableEndBottomDatum,
  [gableEndTopDatumId]: defaultGableEndTopDatum,
  [wallStartDatumId]: defaultWallStartDatum,
  [wallEndDatumId]: defaultWallEndDatum,
} as const;

//type DefaultFacadeDatumDataId = keyof typeof defaultFacadeDatumsInternal;
export const defaultFacadeDatumDataInternalIds: FacadeDatumTable = defaultFacadeDatumsInternal;

/**
 * Projects a Vec3 into a face plane, then casts a Ray
 * from that point and returns the intersections with the outer loop of the face
 * as a PolyLine2 if the intersection exists
 */
export function facadeSweepRail2FromRayInFace(
  sweepFace: Face,
  originX: WorldInches,
  originY: WorldInches,
  originZ: WorldInches,
  angle: number,
  mirror: boolean,
): PolyLine2 | null {
  const { plane, loops } = sweepFace;

  const x = mirror ? -1 : 1;
  const rotationAngle = mirror ? -angle : angle;

  const [u, v] = planeProject(plane, originX, originY, originZ);
  const [vX, vY] = vec2Rotate(x, 0, degreeToRadian(rotationAngle));

  const ints = intersectRay2PolyLine2(loops[0], u, v, vX, vY);
  ray2PolyLine2IntersectionMakeUnique(ints);
  if (ints.length !== 2) {
    // this ray does not intersect the face, or only intersects at a point
    return null;
  }

  const { x: intx1p, y: inty1p } = ints[0];
  const { x: intx2p, y: inty2p } = ints[1];

  const intx1 = intx1p;
  const inty1 = inty1p;
  const intx2 = intx2p;
  const inty2 = inty2p;

  const finalPl = polyLine2ByTwoPoints(intx1, inty1, intx2, inty2);
  return finalPl;
}

export function facadeSweepRail2FromFaceBounds(sweepFace: Face, sweepRailZ: WorldInches, mirror: boolean): PolyLine2 {
  const { loops, plane } = sweepFace;

  const { minX, maxX } = polyLine2AABB2(loops[0]);
  const plY = sweepRailZ - plane.originZ;

  const intx1 = mirror ? maxX : minX;
  const inty1 = plY;
  const intx2 = mirror ? minX : maxX;
  const inty2 = plY;

  const finalPl = polyLine2ByTwoPoints(intx1, inty1, intx2, inty2);
  return finalPl;
}

export function applyExtensionsToFacadeSweepRail2(facadeSweepRail2: PolyLine2, startExtension = 0, endExtension = 0) {
  const railLen = polyLine2Length(facadeSweepRail2);

  // guard against NaN output from offsets
  let so = startExtension;
  if (so === -railLen) {
    so -= ZERO_EPSILON;
  }
  let eo = endExtension;
  if (eo === -railLen) {
    eo -= ZERO_EPSILON;
  }
  if (Math.abs(so) < DIST_EPSILON && Math.abs(eo) < DIST_EPSILON) {
    return facadeSweepRail2;
  }
  polyLine2Extend(facadeSweepRail2, so, eo);
  return undefined;
}

export function removeFormulaDisabledStrata<T extends FacadeSweepStratum | FacadeSpanStratum>(
  tableItems: Table<T>,
): Table<T> {
  const filtered: RWTable<T> = {};

  for (const id in tableItems) {
    const item = tableItems[id];
    if (!item) {
      continue;
    }

    if (item.disabled) {
      continue;
    }

    filtered[id] = item;
  }

  return filtered;
}

export function collectAllWallDatums(datumAbstractions: Table<DatumAbstraction>): Table<WallDatumAbstraction> {
  const wallDatums: RWTable<WallDatumAbstraction> = {};
  for (const datumId in datumAbstractions) {
    const datum = datumAbstractions[datumId];
    if (datum?.isValidForUsage(DatumVerticalUsageType.WALL_VERTICAL)) {
      wallDatums[datumId] = datum;
      continue;
    }
    if (datum?.isValidForUsage(DatumHorizontalUsageType.WALL_HORIZONTAL)) {
      wallDatums[datumId] = datum;
    }
  }

  return wallDatums;
}

export function collectWallFacadeDatums(
  datumAbstractions: Table<DatumAbstraction>,
  wallFacadeSpans: FacadeSpanStratum[],
  wallFacadeSweeps: FacadeSweepStratum[],
  componentInstanceChain?: string[],
): Table<WallDatumAbstraction> {
  const wallFacadeDatums: RWTable<WallDatumAbstraction> = {};
  for (const span of wallFacadeSpans) {
    const { lowerDatumUsage, upperDatumUsage, startDatumUsage, endDatumUsage } = span;

    const lower = getDatumAbstraction({
      datumAbstractions,
      datumUsage: lowerDatumUsage,
      requestedUsageType: DatumVerticalUsageType.WALL_VERTICAL,
      componentInstanceChain,
    });

    const upper = getDatumAbstraction({
      datumAbstractions,
      datumUsage: upperDatumUsage,
      requestedUsageType: DatumVerticalUsageType.WALL_VERTICAL,
      componentInstanceChain,
    });

    const start = getDatumAbstraction({
      datumAbstractions,
      datumUsage: startDatumUsage,
      requestedUsageType: DatumHorizontalUsageType.WALL_HORIZONTAL,
      componentInstanceChain,
    });

    const end = getDatumAbstraction({
      datumAbstractions,
      datumUsage: endDatumUsage,
      requestedUsageType: DatumHorizontalUsageType.WALL_HORIZONTAL,
      componentInstanceChain,
    });

    if (lower?.isValidForUsage(DatumVerticalUsageType.WALL_VERTICAL)) {
      wallFacadeDatums[lowerDatumUsage.entityId] = lower;
    }
    if (upper?.isValidForUsage(DatumVerticalUsageType.WALL_VERTICAL)) {
      wallFacadeDatums[upperDatumUsage.entityId] = upper;
    }
    if (start?.isValidForUsage(DatumHorizontalUsageType.WALL_HORIZONTAL)) {
      wallFacadeDatums[startDatumUsage.entityId] = start;
    }
    if (end?.isValidForUsage(DatumHorizontalUsageType.WALL_HORIZONTAL)) {
      wallFacadeDatums[endDatumUsage.entityId] = end;
    }
  }

  for (const sweep of wallFacadeSweeps) {
    const { datumUsage } = sweep;
    const resolved = getDatumAbstraction({
      datumAbstractions,
      datumUsage,
      requestedUsageType: DatumVerticalUsageType.WALL_VERTICAL,
      componentInstanceChain,
    });

    if (resolved?.isValidForUsage(DatumVerticalUsageType.WALL_VERTICAL)) {
      wallFacadeDatums[datumUsage.entityId] = resolved;
    }
  }

  return wallFacadeDatums;
}

export function iterateFacadesForHalfEdge(
  wallFacadeTable: WallFacadeTable,
  labels: HalfEdgeAssociationTable<Label>,
  edge: number,
  callbackForFacade: (facade?: Facade) => boolean,
  callbackForSideResult?: () => boolean,
) {
  // Check for supression/addition via facade
  const labelsForEdgeSides = [labels[edge], labels[planarGraphAdjacentHalfEdge(edge)]];
  for (const labelsForEdge of labelsForEdgeSides) {
    if (!labelsForEdge || labelsForEdge.length === 0) {
      continue;
    }

    // iterate in reverse order (last added facade has higher priority)
    for (let i = labelsForEdge.length - 1; i >= 0; i--) {
      const facadeId = labelsForEdge[i].item.facadeId;
      const facade: Facade | undefined = wallFacadeTable[facadeId];

      if (callbackForFacade(facade)) {
        return;
      }
    }
    if (callbackForSideResult && callbackForSideResult()) {
      break;
    }
  }
}
